import { fromActions } from '@rxsv/core';
import type { OperatorFunction } from 'rxjs';
import { map } from 'rxjs/operators';
import { hasKey } from '@bayer-int/portletce-shared';

import * as FiltersSetting from './filters-settings';
import * as EmailSupport from './email-support';
import * as View from './view';
import * as Auth from './auth';
import * as Init from './init';
import * as Performance from './performance';
import * as TA from './triggersAndActions';

export type AppAction =
    | FiltersSetting.Action
    | EmailSupport.Action
    | View.Action
    | Init.Action
    | Auth.Action
    | TA.Action
    | Performance.Action;

export const AppAction = {
    filters: FiltersSetting.Action,
    email: EmailSupport.Action,
    view: View.Action,
    portlet: Init.Action,
    auth: Auth.Action,
    triggersAndActions: TA.Action,
    performance: Performance.Action,
};

export const AppErrorActions = [
    ...FiltersSetting.ErrorActions,
    ...EmailSupport.ErrorActions,
    ...View.ErrorActions,
    ...Auth.ErrorActions,
    ...Init.ErrorActions,
    ...TA.ErrorActions,
];

/**
 * Make sure that `allowedExternalActionsFilter` and `publishableActionsFilter`
 * have no overlap, otherwise it may cause endless loops
 */

export const allowedExternalActionsFilter = fromActions(
    FiltersSetting.Action.SET_FILTER_VALUE,
    FiltersSetting.Action.SET_FILTER_VALUES,
    FiltersSetting.Action.CLEAR_FILTER,
    FiltersSetting.Action.CLEAR_FILTERS,
    FiltersSetting.Action.CHANGE_SETTING_VALUES,
    FiltersSetting.Action.CLEAR_SETTING_VALUES,
    FiltersSetting.Action.FORCE_LOAD_LAZY_SETTING_DEFINITION,
    FiltersSetting.Action.FORCE_LOAD_LAZY_ALL_SETTING_DEFINITIONS,
    TA.Action.TA_RECEIVE_NAVIGATION_OPTIONS_FOR_DATATIP,
    Init.Action.EXTERNAL_REINITIALIZATION,
    FiltersSetting.Action.APPLIED_FILTER_VALUES,
);

export const publishableActionsFilter = fromActions(
    FiltersSetting.Action.CHANGE_FILTER_VALUE,
    FiltersSetting.Action.CHANGE_FILTER_VALUES,
    FiltersSetting.Action.EMIT_LISTEN_FILTERS,
    FiltersSetting.Action.EMIT_SETTINGS_FORM,
    FiltersSetting.Action.CLEAR_FILTER_REQUEST,
    FiltersSetting.Action.CLEAR_FILTERS_REQUEST,
    Init.Action.PORTLET_VISUALIZATION_STATE_CHANGE,
    TA.Action.TA_REQUEST_NAVIGATION_OPTIONS_FOR_DATATIP,
    TA.Action.TA_NAVIGATION_DATATIP_CLICK,
    Init.Action.CHART_RENDERED,
);

export const listenerActionsFilter = fromActions(
    Init.Action.PORTLET_INIT,
    Init.Action.PORTLET_RESET,
    Init.Action.PORTLET_INIT_SUCCESS,
    Init.Action.PORTLET_INIT_ERROR,
    Init.Action.PORTLET_VISUALIZATION_STATE_CHANGE,
    FiltersSetting.Action.CHANGE_SETTING_VALUE,
    FiltersSetting.Action.CHANGE_SETTING_VALUES,
    FiltersSetting.Action.CHANGE_FILTER_VALUE,
    FiltersSetting.Action.CHANGE_FILTER_VALUES,
    FiltersSetting.Action.CLEAR_FILTER,
    FiltersSetting.Action.CLEAR_FILTERS,
    FiltersSetting.Action.CLEAR_FILTER_REQUEST,
    FiltersSetting.Action.CLEAR_FILTERS_REQUEST,
    Init.Action.CHART_RENDERED,
    Init.Action.CHART_RENDER_ERROR,
);

type AllowedExternalActions = typeof allowedExternalActionsFilter extends OperatorFunction<
    AppAction,
    infer A
>
    ? A
    : never;

type PublishableActions = typeof publishableActionsFilter extends OperatorFunction<
    AppAction,
    infer A
>
    ? A
    : never;

type DOMEventActions = typeof listenerActionsFilter extends OperatorFunction<AppAction, infer A>
    ? A
    : never;

export type PublicAction = AllowedExternalActions | PublishableActions;
export type ExternalAppAction = PublicAction & { sender: string };

export const attachSenderToPublishableAction = (
    sender: string,
): OperatorFunction<PublicAction, ExternalAppAction> => map(action => ({ ...action, sender }));

export const actionToDOMEventNameMapping: Record<DOMEventActions['type'], string> = {
    PORTLET_INIT: 'portlet-init',
    PORTLET_RESET: 'portlet-reset',
    PORTLET_INIT_SUCCESS: 'portlet-init-success',
    PORTLET_INIT_ERROR: 'portlet-init-error',
    CHANGE_SETTING_VALUE: 'change-setting-values',
    CHANGE_SETTING_VALUES: 'change-setting-values',
    CHANGE_FILTER_VALUE: 'change-filter-values',
    CHANGE_FILTER_VALUES: 'change-filter-values',
    CLEAR_FILTER: 'clear-filters',
    CLEAR_FILTERS: 'clear-filters',
    CLEAR_FILTER_REQUEST: 'clear-filters-request',
    CLEAR_FILTERS_REQUEST: 'clear-filters-request',
    CHART_RENDERED: 'chart-rendered',
    CHART_RENDER_ERROR: 'chart-render-error',
    PORTLET_VISUALIZATION_STATE_CHANGE: 'portlet-visualization-state-change',
};

export const listenerActionToDomEvent = map<DOMEventActions, CustomEvent>(action => {
    const eventName = actionToDOMEventNameMapping[action.type];
    const detail = hasKey(action, 'payload') ? action.payload : null;

    switch (action.type) {
        case 'CHANGE_FILTER_VALUE':
            return new CustomEvent(eventName, { detail: [detail] });
        case 'CHANGE_SETTING_VALUE':
            return new CustomEvent(eventName, { detail: [detail] });
        case 'CLEAR_FILTER':
            return new CustomEvent(eventName, { detail: [detail] });
        case 'CLEAR_FILTER_REQUEST':
            return new CustomEvent(eventName, { detail: [detail] });
        default:
            return new CustomEvent(eventName, { detail });
    }
});
